import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Bell, Check, Route, UserPlus, MessageSquare, Info } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
const TYPE_META = {
  application: { Icon: UserPlus, bg: "bg-primary/15", color: "text-primary" },
  chat: { Icon: MessageSquare, bg: "bg-emerald-500/15", color: "text-emerald-400" },
  chat_group: { Icon: MessageSquare, bg: "bg-emerald-500/15", color: "text-emerald-400" },
  system: { Icon: Info, bg: "bg-slate-500/15", color: "text-slate-400" },
};

const typeMeta = (type) => TYPE_META[type] || { Icon: Info, bg: "bg-slate-500/15", color: "text-slate-400" };

const formatNotifItem = (n) => {
  const rawText = n.text || "";
  const cleanText = rawText
    .replace(/\s+in\s+['"]Direktnachricht['"]/gi, "")
    .replace(/\s+in\s+Direktnachricht/gi, "");

  const msgContent = n.data?.message;
  let title = n.title || cleanText;
  let body = "";

  if (msgContent && msgContent !== cleanText) {
    title = cleanText;
    body = msgContent;
  }

  return { title, body };
};

export default function NotificationBell({ showLabel = false }) {
  const { token } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0, width: 320, maxH: 384 });
  const btnRef = useRef(null);
  const wasOpenRef = useRef(false);
  const unread = notifs.filter(n => !n.read).length;

  const fetchNotifs = () => {
    if (!token) return;
    api.notifications.list()
      .then(r => {
        const list = Array.isArray(r) ? r : [];
        setNotifs(prev => {
          if (prev.length === list.length && JSON.stringify(prev) === JSON.stringify(list)) {
            return prev;
          }
          return list;
        });
      })
      .catch(() => {});
  };

  useEffect(() => {
    let interval;
    const start = () => { if (!interval) interval = setInterval(fetchNotifs, 10000); };
    const stop = () => { clearInterval(interval); interval = null; };
    const onVisibility = () => {
      if (document.hidden) stop(); else { fetchNotifs(); start(); }
    };
    fetchNotifs();
    start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [token]);

  const updatePosition = () => {
    if (!btnRef.current) return;
    const dropdownWidth = Math.min(420, window.innerWidth - 32);

    if (window.innerWidth < 1024) {
      setPos({
        top: 72,
        left: undefined,
        right: 16,
        width: dropdownWidth,
        maxH: Math.min(384, window.innerHeight - 100)
      });
    } else {
      setPos({
        top: 80,
        left: undefined,
        right: 16,
        width: dropdownWidth,
        maxH: Math.min(480, window.innerHeight - 100)
      });
    }
  };

  const handleToggle = () => {
    if (!open) updatePosition();
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return;
    const onResize = () => updatePosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  const markAllRead = useCallback(async () => {
    try {
      await api.notifications.readAll();
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.warn('[NotificationBell] Failed to mark notifications read:', err);
    }
  }, [token]);

  useEffect(() => {
    if (!open && wasOpenRef.current && unread > 0) {
      markAllRead();
    }
    wasOpenRef.current = open;
  }, [open, unread, markAllRead]);

  const formatTime = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) + ' Uhr';
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleToggle}
        className={`relative flex items-center gap-4 h-11 pl-[14px] pr-3 w-full rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${open ? 'bg-primary/10 text-primary' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
        title={showLabel ? "" : "Benachrichtigungen"}
      >
        <div className="relative flex-shrink-0">
          <Bell size={20} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full border-2 border-zinc-950 animate-pulse" />
          )}
        </div>
        <AnimatePresence>
          {showLabel && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2, delay: 0.15 }}
              className="overflow-hidden whitespace-nowrap"
            >
              Benachrichtigungen
            </motion.span>
          )}
        </AnimatePresence>
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[101]" onClick={() => setOpen(false)} />
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 380, damping: 28 }}
              className="fixed frosted-card !rounded-2xl !p-0 shadow-[0_40px_100px_rgba(0,0,0,0.9)] z-[102] overflow-hidden border border-white/10"
              style={{
                top: pos.top,
                left: pos.left,
                right: pos.right,
                width: pos.width,
              }}
            >
              <div className="h-1 w-full bg-primary" />
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/5 bg-primary/[0.06]">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
                    <Bell size={15} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-white uppercase tracking-[0.2em] leading-none">Info-Zentrale</h3>
                    {unread > 0 && (
                      <p className="text-[9px] font-bold text-primary uppercase tracking-tighter mt-0.5">{unread} neu</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={fetchNotifs}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/10 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="Aktualisieren"
                >
                  <Route size={13} className="rotate-90" />
                </button>
              </div>

              {/* List */}
              <div
                className="overflow-y-auto no-scrollbar divide-y divide-white/5"
                style={{ maxHeight: pos.maxH }}
              >
                {notifs.length === 0 ? (
                  <div className="p-10 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary/10 to-transparent flex items-center justify-center">
                      <Bell size={26} className="text-slate-700 opacity-30" />
                    </div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 tracking-[0.2em] opacity-50">Keine neuen Meldungen</p>
                  </div>
                ) : (
                  notifs.slice(0, 30).map(n => {
                    const meta = typeMeta(n.type);
                    const Icon = meta.Icon;
                    const { title, body } = formatNotifItem(n);
                    return (
                      <div
                        key={n.id}
                        className={`group/notif flex items-start gap-3 p-3.5 border-l-2 transition-all ${!n.read ? 'border-primary bg-primary/[0.06] hover:bg-primary/[0.09]' : 'border-transparent opacity-85 hover:opacity-100 hover:bg-white/[0.03]'}`}
                      >
                        <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${meta.bg}`}>
                          <Icon size={16} className={meta.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-xs font-bold tracking-tight break-words leading-snug pr-1 ${!n.read ? 'text-white font-bold' : 'text-slate-200'}`}>
                              {title}
                            </p>
                            <span className="text-[9px] font-bold text-slate-400 shrink-0 mt-0.5 whitespace-nowrap">{formatTime(n.created_at)}</span>
                          </div>
                          {body && (
                            <p className={`text-[11px] leading-relaxed mt-1 break-words ${!n.read ? 'text-slate-300' : 'text-slate-400'}`}>
                              {body}
                            </p>
                          )}
                        </div>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              {unread > 0 && (
                <div className="p-3 border-t border-white/5 bg-white/[0.01]">
                  <button
                    onClick={markAllRead}
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary text-[10px] font-bold uppercase tracking-[0.2em] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Check size={13} /> Alles gelesen
                  </button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </>,
        document.body
      )}
    </>
  );
}
